import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";
import { COMPANY, SERVICES, NAV_LINKS } from "@/lib/content";

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const onSubmit = (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    // PLACEHOLDER: wire up to Web3Forms / newsletter provider.
    toast.success("Thanks for subscribing — we'll be in touch.");
    setEmail("");
  };
  return (
    <form onSubmit={onSubmit} className="flex items-center gap-2 max-w-sm" data-testid="newsletter-form">
      <div className="relative flex-1">
        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Work email"
          data-testid="newsletter-email"
          className="w-full rounded-full glass pl-10 pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      <button
        type="submit"
        data-testid="newsletter-submit"
        className="rounded-full brand-gradient-bg text-white p-3 hover:-translate-y-0.5 transition-transform"
        aria-label="Subscribe"
      >
        <ArrowUpRight className="h-4 w-4" />
      </button>
    </form>
  );
};

export const Footer = () => {
  const legal = [
    { label: "Privacy Policy", to: "/privacy" },
    { label: "Terms", to: "/terms" },
    { label: "Cookie Policy", to: "/cookies" },
  ];
  return (
    <footer className="relative overflow-hidden border-t border-border bg-background" data-testid="footer">
      <div className="noise !opacity-0 dark:!opacity-[0.05]" />
      <div className="glow brand-gradient-bg opacity-10 h-72 w-72 -bottom-20 left-1/4" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-20 pb-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo />
            <p className="mt-5 text-sm text-muted-foreground leading-relaxed max-w-xs">
              {COMPANY.tagline} Senior-led consulting across integration, cloud, AI and data — accountable to your outcomes.
            </p>
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">
                Subscribe to our newsletter
              </p>
              <NewsletterForm />
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-sm font-semibold mb-4">Services</h4>
              <ul className="space-y-2.5">
                {SERVICES.slice(0, 6).map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4">Company</h4>
              <ul className="space-y-2.5">
                {NAV_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4">Get in touch</h4>
              <ul className="space-y-2.5 text-sm text-muted-foreground">
                <li><a href={`mailto:${COMPANY.email}`} className="hover:text-primary transition-colors">{COMPANY.email}</a></li>
                <li>{COMPANY.address}</li>
                <li className="flex gap-3 pt-2">
                  <a href={COMPANY.social.linkedin} className="hover:text-primary transition-colors">LinkedIn</a>
                  <a href={COMPANY.social.twitter} className="hover:text-primary transition-colors">X</a>
                  <a href={COMPANY.social.github} className="hover:text-primary transition-colors">GitHub</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Massive brand statement */}
        <Link to="/contact" className="block mt-16 group" data-testid="footer-lets-talk">
          <span className="font-display font-semibold tracking-tighter leading-none text-[16vw] lg:text-[11rem] brand-gradient-text inline-flex items-center gap-4">
            Let’s talk
            <ArrowUpRight className="h-[10vw] w-[10vw] lg:h-24 lg:w-24 text-primary transition-transform duration-500 group-hover:translate-x-4 group-hover:-translate-y-4" />
          </span>
        </Link>

        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </p>
          <div className="flex gap-6">
            {legal.map((l) => (
              <Link key={l.to} to={l.to} className="text-xs text-muted-foreground hover:text-primary transition-colors">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
